import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete } from '@material-ui/lab';
import { filterOptions, getOptionSelected, getOptionsMap } from './utils';
import SelectedOptionChip from './SelectedOptionChip';
import InputTextField from './InputTextField';

const MultiSelectChips = ({
  className,
  handleOnChange,
  id,
  label,
  limitTags,
  maxSelections,
  options = [],
  placeholder,
  values,
  ...rest
}) => {
  const [customOption, setCustomOption] = useState('');
  const optionsMapRef = useRef(getOptionsMap(options));

  const hasMaxValueCount =
    maxSelections !== undefined && maxSelections !== null;

  const isValuesLocked = hasMaxValueCount && values.length >= maxSelections;

  const handleChange = (_e, selectedOptions) => {
    if (hasMaxValueCount && selectedOptions.length > maxSelections) {
      return;
    }

    const nextValues = selectedOptions.map((option) => {
      const value = option?.value ?? option;
      return value.trim?.() || value;
    });

    handleOnChange(nextValues);
  };

  return (
    <Autocomplete
      className={className}
      clearOnBlur
      disableCloseOnSelect
      filterOptions={filterOptions(values)}
      getOptionDisabled={(option) => {
        const value = option?.value ?? option;
        return isValuesLocked && !values.includes(value);
      }}
      getOptionLabel={(option) => option?.label || option}
      getOptionSelected={getOptionSelected}
      id={id}
      inputValue={customOption}
      limitTags={limitTags}
      onInputChange={(_event, newInputValue) => {
        setCustomOption(newInputValue.trimStart());
      }}
      multiple
      onChange={handleChange}
      options={options}
      freeSolo
      renderTags={(values, getTagProps) =>
        values.map((value, index) => {
          const label = value?.label || optionsMapRef.current[value] || value;
          return (
            <SelectedOptionChip
              key={label}
              label={label}
              tagProps={getTagProps({ index })}
            />
          );
        })
      }
      renderInput={(params) => (
        <InputTextField
          label={label}
          params={{
            ...params,
            inputProps: {
              ...params.inputProps,
              readOnly: isValuesLocked,
            },
          }}
          placeholder={placeholder}
        />
      )}
      value={values}
      {...rest}
    />
  );
};

MultiSelectChips.propTypes = {
  className: PropTypes.string,
  handleOnChange: PropTypes.func,
  id: PropTypes.string,
  label: PropTypes.string,
  limitTags: PropTypes.number,
  maxSelections: PropTypes.number,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ])
  ),
  placeholder: PropTypes.string,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
};

export default MultiSelectChips;
